import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  ScrollText,
  Search,
  Lock,
  Unlock,
  Pencil,
  Plus,
  Trash2,
  RotateCcw,
} from "lucide-react";

const ACTION_META = {
  create: { icon: Plus, label: "Created", color: "text-[#2d5e1f] bg-[#e8f6e2] border-[#6cc850]" },
  update: { icon: Pencil, label: "Updated", color: "text-[#39678f] bg-[#e6f3ff] border-[#39678f]" },
  lock: { icon: Lock, label: "Locked", color: "text-[#4B4B9D] bg-[#ececf7] border-[#4B4B9D]" },
  unlock: { icon: Unlock, label: "Unlocked", color: "text-[#8a6d00] bg-[#f5ead0] border-[#8a6d00]" },
  delete: { icon: Trash2, label: "Soft-deleted", color: "text-[#b30035] bg-[#fbe4ea] border-[#b30035]" },
  restore: { icon: RotateCcw, label: "Restored", color: "text-[#2d5e1f] bg-[#e8f6e2] border-[#6cc850]" },
};

const FIELD_LABELS = {
  job_number: "Job/Freezer №",
  vessel_type: "Vessel type",
  vessel_number: "Vessel №",
  test_result: "Test result",
  test_pressure: "Test pressure",
  test_medium: "Test medium",
  test_person_id: "Test person",
  test_date: "Test date",
  failure_points: "Failure points",
  welder1_id: "Welder 1",
  welder2_id: "Welder 2",
  blown_out_employee_id: "Blown out by",
  blown_out_date: "Blown out date",
  notes: "Notes",
};

const FILTERS = [
  { key: "lock,unlock", label: "Lock / Unlock" },
  { key: "", label: "All events" },
  { key: "create", label: "Created" },
  { key: "update", label: "Updated" },
  { key: "delete,restore", label: "Delete / Restore" },
];

function fmt(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

export default function AuditTrail() {
  const [filter, setFilter] = useState("lock,unlock");
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/audit-log/all", { params: { action: filter || undefined, limit: 300 } })
      .then((res) => setEntries(res.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const visible = useMemo(() => {
    if (!entries) return [];
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        (e.record_label || "").toLowerCase().includes(q) ||
        (e.actor_name || "").toLowerCase().includes(q),
    );
  }, [entries, search]);

  return (
    <div className="p-4 md:p-10" data-testid="audit-trail-page">
      <div className="mb-6">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <ScrollText className="w-3.5 h-3.5" /> Compliance
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
          AUDIT TRAIL
        </h1>
        <p className="font-body text-sm text-[#50493b] mt-3">
          Every create, update, lock, unlock, delete and restore across the registry. Defaults to
          lock &amp; unlock events for compliance review.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              data-testid={`audit-filter-${f.key || "all"}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 h-9 text-xs subtitle uppercase tracking-wider border transition-colors ${
                filter === f.key
                  ? "bg-[#33424f] text-white border-[#33424f]"
                  : "bg-white text-[#50493b] border-[#33424f]/25 hover:border-[#33424f]/60"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative lg:ml-auto lg:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#50493b]" />
          <Input
            data-testid="audit-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job number or user…"
            className="pl-9 border-[#33424f]/30 h-9"
          />
        </div>
      </div>

      {/* Entries */}
      <div className="space-y-3" data-testid="audit-trail-list">
        {loading && <div className="text-sm text-[#50493b] font-body">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="border border-[#33424f]/15 bg-white p-8 text-center text-sm text-[#50493b] font-body">
            No audit events match this view.
          </div>
        )}
        {!loading &&
          visible.map((e) => {
            const meta = ACTION_META[e.action] || ACTION_META.update;
            const Icon = meta.icon;
            const changeKeys = Object.keys(e.changes || {});
            return (
              <div
                key={e.id}
                className="border border-[#33424f]/15 bg-white p-4"
                data-testid={`audit-row-${e.action}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                    <div className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] subtitle uppercase tracking-[0.2em] ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </div>
                    <div className="font-display text-sm text-[#33424f] tracking-tight truncate">
                      {e.record_label}
                      {e.record_vessel_number != null && (
                        <span className="text-[#50493b] font-body">
                          {" "}
                          · {e.record_vessel_type} #{e.record_vessel_number}
                        </span>
                      )}
                      {e.record_deleted && (
                        <span className="ml-2 text-[10px] subtitle uppercase tracking-wider text-[#b30035]">
                          deleted
                        </span>
                      )}
                    </div>
                    <div className="font-body text-xs text-[#50493b] truncate">
                      by <span className="subtitle text-[#33424f]">{e.actor_name || "—"}</span>
                    </div>
                  </div>
                  <div className="font-body text-xs text-[#50493b] tabular-nums shrink-0">
                    {new Date(e.timestamp).toLocaleString()}
                  </div>
                </div>

                {/* Lock detail */}
                {e.action === "lock" && (
                  <div className="mt-3 text-xs font-body text-[#33424f]">
                    Blown out by{" "}
                    <span className="subtitle">{e.blown_out_name || "—"}</span> on{" "}
                    <span className="subtitle">{e.blown_out_date || "—"}</span>
                  </div>
                )}
                {e.action === "unlock" && (
                  <div className="mt-3 text-xs font-body text-[#50493b]">
                    Record unlocked — blown-out history preserved in the record notes.
                  </div>
                )}

                {/* Update change rows */}
                {changeKeys.length > 0 && (
                  <ul className="mt-3 space-y-2 sm:space-y-1 text-xs font-body">
                    {changeKeys.map((k) => (
                      <li key={k} className="flex flex-col sm:grid sm:grid-cols-[140px,1fr] sm:gap-3 gap-1">
                        <span className="subtitle text-[#50493b] uppercase tracking-[0.15em] text-[10px]">
                          {FIELD_LABELS[k] || k}
                        </span>
                        <span className="text-[#33424f] break-words">
                          <span className="line-through text-[#b30035]/80">{fmt(e.changes[k].old)}</span>
                          <span className="mx-2 text-[#50493b]">→</span>
                          <span className="subtitle">{fmt(e.changes[k].new)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
